/*
 * product-configurator.js
 * Migrated from the Live theme's ProductForm logic.
 *
 * Adds three behaviours to <product-form-component> on a product page:
 *   1. A live total price that reflects the base variant + addon selections.
 *   2. Quantity-card toggling and +/- counter buttons for `enable_quantity` items.
 *   3. Bundle add-to-cart: when the customer submits the form with addons selected,
 *      we POST a multi-item payload to /cart/add.js, tying everything together
 *      with a shared `_bundle_id` line-item property.
 *
 * The script is dedupe-safe: each block can include the <script> tag, the IIFE
 * guards against double-loading, and each <product-form-component> is initialized
 * only once. It deliberately uses no ES module imports so the file can be loaded
 * directly via `{{ 'product-configurator.js' | asset_url }}`.
 */

// EARLY MARKER: this should appear in the console as soon as the file is loaded,
// regardless of whether init succeeds. If you don't see this, the script tag
// isn't being rendered or the asset URL is failing.
try { console.log('[product-configurator] file fetched, version 2026-05-06'); } catch (_) {}

(function () {
  'use strict';

  if (window.__productConfiguratorLoaded) {
    try { console.log('[product-configurator] already loaded, skipping IIFE'); } catch (_) {}
    return;
  }
  window.__productConfiguratorLoaded = true;

  var DEBUG = true; // Set to false once everything is verified.
  function log() {
    if (!DEBUG) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[product-configurator]');
      console.log.apply(console, args);
    } catch (_) {}
  }
  log('IIFE running');

  var SELECTORS = {
    productForm: 'product-form-component',
    cardInput: '.config-card__input',
    quantityItem: '.config-quantity-item',
    quantityControl: '.config-quantity-control',
    quantityBtn: '.config-quantity-control__btn',
    quantityDisplay: '.config-quantity-control__display',
    quantityHidden: '.config-quantity-item__hidden',
    productPrice: 'product-price',
    variantIdInput: 'input[name="id"]',
    formQuantityInput: 'input[name="quantity"]',
  };

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var format = (window.theme && window.theme.moneyFormat) || '€{{amount_with_comma_separator}}';
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (_) {
        // fall through to default formatter
      }
    }
    var euros = (cents / 100).toFixed(2).replace('.', ',');
    return '€ ' + euros;
  }

  function findClosestSection(el) {
    // NOTE: do NOT match `[data-section-id]` here — savor puts that attribute
    // directly on <product-form-component>, so closest() would return the
    // product-form-component itself and we'd never find sibling .config-group
    // blocks. We want the actual section/page wrapper above it.
    return el.closest('.shopify-section, dialog, .product-information') || document;
  }

  /**
   * One configurator instance per <product-form-component>.
   */
  function ProductConfigurator(productForm) {
    this.productForm = productForm;
    this.form = productForm.querySelector('form');
    if (!this.form) return;

    this.section = findClosestSection(productForm);
    this.basePriceCents = this.readBasePriceFromDOM();
    this.priceObserver = null;

    this.bindEvents();
    this.refreshTotalPrice();
  }

  ProductConfigurator.prototype.readBasePriceFromDOM = function () {
    // Preferred: a block injects <span data-config-base-price="{{ price }}">.
    var explicitEl = this.section.querySelector('[data-config-base-price]');
    if (explicitEl) {
      var raw = explicitEl.getAttribute('data-config-base-price');
      var asInt = parseInt(raw, 10);
      if (!isNaN(asInt)) return asInt;
    }

    // Legacy: data-base-price on the <product-form-component> element itself.
    var legacy = this.productForm.dataset.basePrice;
    if (legacy && !isNaN(parseInt(legacy, 10))) {
      return parseInt(legacy, 10);
    }

    // Fall back to whatever savor's <product-price> currently shows.
    var priceEl = this.findPriceElement();
    if (priceEl) {
      var parsed = this.parsePriceText(priceEl.textContent || '');
      if (parsed != null) return parsed;
    }
    return 0;
  };

  ProductConfigurator.prototype.parsePriceText = function (text) {
    // Strip non-digit, non-decimal-separator characters and parse to cents.
    var digits = String(text).replace(/[^0-9.,-]/g, '').trim();
    if (!digits) return null;
    // Normalize: drop thousands separators, treat last comma/dot as decimal.
    var normalized = digits.replace(/[\s]/g, '');
    // Use a heuristic: the last comma OR dot is the decimal mark.
    var lastComma = normalized.lastIndexOf(',');
    var lastDot = normalized.lastIndexOf('.');
    var decIdx = Math.max(lastComma, lastDot);
    if (decIdx === -1) {
      // No decimal — treat as whole units, multiply by 100.
      var asInt = parseInt(normalized, 10);
      return isNaN(asInt) ? null : asInt * 100;
    }
    var intPart = normalized.slice(0, decIdx).replace(/[.,]/g, '');
    var decPart = normalized.slice(decIdx + 1).replace(/[^0-9]/g, '').slice(0, 2);
    while (decPart.length < 2) decPart += '0';
    var cents = parseInt(intPart, 10) * 100 + parseInt(decPart, 10);
    return isNaN(cents) ? null : cents;
  };

  ProductConfigurator.prototype.findPriceElement = function () {
    var productPrice = this.section.querySelector(SELECTORS.productPrice);
    if (!productPrice) return null;

    // Savor's price snippet renders <div class="price__regular"><span class="price">...</span></div>
    // for the regular case, and <span class="price-item--sale price"> when on sale.
    // Prefer the visible (non-hidden) wrapper, then drill to the inner span.
    var notHidden = function (sel) {
      var nodes = productPrice.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (!n.classList.contains('price__hidden') && !n.classList.contains('visually-hidden')) {
          return n;
        }
      }
      return null;
    };

    var saleWrap = notHidden('.price__sale');
    if (saleWrap) {
      return (
        saleWrap.querySelector('.price-item--sale.price') ||
        saleWrap.querySelector('.price-item--sale') ||
        saleWrap.querySelector('.price') ||
        saleWrap
      );
    }
    var regularWrap = notHidden('.price__regular');
    if (regularWrap) {
      return regularWrap.querySelector('.price') || regularWrap;
    }

    // Fallbacks for other layouts (volume pricing, custom).
    return (
      productPrice.querySelector('[ref="priceContainer"] .price') ||
      productPrice.querySelector('.price-item--regular') ||
      productPrice.querySelector('.price-item--sale') ||
      productPrice.querySelector('.price-item') ||
      productPrice.querySelector('[ref="priceContainer"]') ||
      productPrice
    );
  };

  ProductConfigurator.prototype.bindEvents = function () {
    var self = this;

    // NOTE: form-level submit interception is NOT enough — savor's component
    // framework registers a document-level capture listener for `submit` (see
    // assets/component.js), which fires before any form-level listener.
    // We use a window-level capture listener (registered once globally below)
    // that fires before document-level capture, see installGlobalSubmitGuard().

    // Config-group blocks are siblings of <product-form-component> in savor —
    // they live in the same _product-details container. Scope listeners to the
    // section so we catch their clicks/changes.
    this.section.addEventListener('click', function (e) { self.handleClick(e); });

    this.section.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches(SELECTORS.cardInput)) {
        self.refreshTotalPrice();
      }
    });

    // Variant updates — savor dispatches `variant:update` on the section.
    this.section.addEventListener('variant:update', function (e) {
      var variant = e.detail && e.detail.resource;
      if (variant && typeof variant.price === 'number') {
        self.basePriceCents = variant.price;
      }
      // Defer one tick so savor's own listener replaces the price element first.
      setTimeout(function () { self.refreshTotalPrice(); }, 0);
    });
  };

  ProductConfigurator.prototype.handleClick = function (e) {
    // 1. Quantity +/- button
    var btn = e.target.closest && e.target.closest(SELECTORS.quantityBtn);
    if (btn) {
      e.preventDefault();
      this.handleQuantityButton(btn);
      return;
    }

    // 2. Click on the quantity card (toggle selection)
    var item = e.target.closest && e.target.closest(SELECTORS.quantityItem);
    if (item && !e.target.closest(SELECTORS.quantityControl)) {
      e.preventDefault();
      this.toggleQuantityItem(item);
    }
  };

  ProductConfigurator.prototype.toggleQuantityItem = function (item) {
    var hidden = item.querySelector(SELECTORS.quantityHidden);
    var display = item.querySelector(SELECTORS.quantityDisplay);

    var willSelect = !item.classList.contains('is-selected');
    item.classList.toggle('is-selected', willSelect);

    if (willSelect) {
      if (hidden) {
        hidden.value = '1';
        hidden.disabled = false;
      }
      if (display) display.value = '1';
    } else {
      if (hidden) {
        hidden.value = '0';
        hidden.disabled = true;
      }
      if (display) display.value = '0';
    }

    this.refreshTotalPrice();
  };

  ProductConfigurator.prototype.handleQuantityButton = function (btn) {
    var action = btn.dataset.action;
    var item = btn.closest(SELECTORS.quantityItem);
    if (!item) return;

    var display = item.querySelector(SELECTORS.quantityDisplay);
    var hidden = item.querySelector(SELECTORS.quantityHidden);
    var current = parseInt(display && display.value, 10);
    if (isNaN(current) || current < 1) current = 1;

    if (action === 'plus') {
      current += 1;
    } else if (action === 'minus') {
      if (current > 1) {
        current -= 1;
      } else {
        // At 1 and pressing minus -> deselect the card entirely.
        item.classList.remove('is-selected');
        if (hidden) {
          hidden.value = '0';
          hidden.disabled = true;
        }
        if (display) display.value = '0';
        this.refreshTotalPrice();
        return;
      }
    }

    if (item.classList.contains('is-selected')) {
      if (display) display.value = String(current);
      if (hidden) hidden.value = String(current);
    }

    this.refreshTotalPrice();
  };

  ProductConfigurator.prototype.getAddonsCents = function () {
    var total = 0;

    // Radio/checkbox addons.
    this.section.querySelectorAll(SELECTORS.cardInput + ':checked').forEach(function (input) {
      var price = parseInt(input.dataset.price || '0', 10);
      if (!isNaN(price)) total += price;
    });

    // Quantity items.
    this.section.querySelectorAll(SELECTORS.quantityItem + '.is-selected').forEach(function (item) {
      var display = item.querySelector(SELECTORS.quantityDisplay);
      if (!display) return;
      var qty = parseInt(display.value || '1', 10);
      var price = parseInt(display.dataset.price || '0', 10);
      if (!isNaN(qty) && !isNaN(price)) total += qty * price;
    });

    return total;
  };

  ProductConfigurator.prototype.refreshTotalPrice = function () {
    var priceEl = this.findPriceElement();
    if (!priceEl) {
      log('refreshTotalPrice: could not find price element');
      return;
    }

    var addons = this.getAddonsCents();
    var total = this.basePriceCents + addons;
    var formatted = formatMoney(total);
    priceEl.textContent = formatted;
    log('refreshTotalPrice:', this.basePriceCents, '+', addons, '=', total, '→', formatted);
  };

  ProductConfigurator.prototype.hasAnySelectedAddon = function () {
    return !!(
      this.section.querySelector(SELECTORS.cardInput + ':checked') ||
      this.section.querySelector(SELECTORS.quantityItem + '.is-selected')
    );
  };

  ProductConfigurator.prototype.collectBundleItems = function () {
    var bundleId = String(Date.now());
    var variantInput = this.form.querySelector(SELECTORS.variantIdInput);
    var qtyInput = this.form.querySelector(SELECTORS.formQuantityInput);
    var mainVariantId = variantInput ? Number(variantInput.value) : null;
    var mainQuantity = Number(qtyInput && qtyInput.value) || 1;

    if (!mainVariantId) {
      throw new Error('Could not determine main variant id');
    }

    var properties = { _bundle_id: bundleId };
    var lineItems = [];

    var checkedInputs = Array.prototype.slice.call(
      this.section.querySelectorAll(SELECTORS.cardInput + ':checked')
    );
    var quantityHiddens = Array.prototype.slice.call(
      this.section.querySelectorAll(SELECTORS.quantityHidden + ':not([disabled])')
    );

    var allInputs = checkedInputs.concat(quantityHiddens);
    var parentName = this.getParentProductName();

    allInputs.forEach(function (input) {
      var addonId = Number(input.dataset.addonId);
      var groupTitle = input.dataset.groupTitle || 'Option';
      var choiceName = input.dataset.title || input.value || '';

      var qty = mainQuantity;
      if (input.classList.contains('config-quantity-item__hidden')) {
        qty = parseInt(input.value, 10) || 0;
      }

      var qtyLabel = qty > 1 ? ' (' + qty + 'x)' : '';
      var label = (choiceName + qtyLabel).trim();

      if (properties[groupTitle]) {
        properties[groupTitle] = properties[groupTitle] + ', ' + label;
      } else {
        properties[groupTitle] = label;
      }

      if (addonId && qty > 0) {
        lineItems.push({
          id: addonId,
          quantity: qty,
          properties: {
            _bundle_id: bundleId,
            _parent_product: parentName,
          },
        });
      }
    });

    // Main product first.
    lineItems.unshift({
      id: mainVariantId,
      quantity: mainQuantity,
      properties: properties,
    });

    return lineItems;
  };

  ProductConfigurator.prototype.getParentProductName = function () {
    var titleEl = this.section.querySelector('h1, .product-title, [data-product-title]');
    return (titleEl && titleEl.textContent && titleEl.textContent.trim()) || 'Hoofdproduct';
  };

  /**
   * Collects all section IDs we need refreshed — the same logic savor's
   * product-form-component uses (every <cart-items-component> on the page).
   */
  ProductConfigurator.prototype.collectCartSectionIds = function () {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  };

  ProductConfigurator.prototype.bundleAddToCart = function () {
    var self = this;
    var items = this.collectBundleItems();
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var addUrl = routeRoot.replace(/\/$/, '') + '/cart/add.js';
    var sectionIds = this.collectCartSectionIds();

    var atc = this.form.querySelector('add-to-cart-component button, button[type="submit"]');
    if (atc) {
      atc.disabled = true;
      atc.setAttribute('aria-busy', 'true');
      atc.classList.add('loading');
      atc.dataset.added = 'true';
    }

    var payload = { items: items };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    // 1. POST /cart/add.js with multi-item bundle + sections request.
    return fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, status: res.status, json: json };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg = (result.json && (result.json.description || result.json.message)) ||
            ('Add to cart failed (' + result.status + ')');
          self.dispatchSavorCartError(msg, result.json && result.json.errors);
          throw new Error(msg);
        }
        // 2. Fetch /cart.js for the up-to-date cart resource (item_count etc).
        return Promise.all([
          Promise.resolve(result.json),
          fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
        ]);
      })
      .then(function (results) {
        var addResponse = results[0];
        var cart = results[1];
        // 3. Dispatch the same shape of event savor's product-form-component dispatches:
        //    `cart:update` with detail.resource = cart, detail.data.sections = response.sections.
        //    cart-drawer-component and cart-items-component listen for this.
        self.dispatchSavorCartAdd(cart, addResponse.sections);
      });
  };

  ProductConfigurator.prototype.dispatchSavorCartAdd = function (cart, sections) {
    var detail = {
      resource: cart,
      sourceId: 'product-configurator',
      data: {
        source: 'product-configurator',
        sections: sections || {},
        itemCount: cart && cart.item_count,
        productId: this.productForm.dataset.productId,
      },
    };
    var event;
    try {
      event = new CustomEvent('cart:update', { bubbles: true, detail: detail });
    } catch (_) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent('cart:update', true, true, detail);
    }
    // Dispatch on document so all top-level listeners (cart-drawer-component,
    // cart-items-component) pick it up. Savor's listeners are registered there.
    document.dispatchEvent(event);
  };

  ProductConfigurator.prototype.dispatchSavorCartError = function (message, errors) {
    var detail = { message: message, errors: errors };
    var event;
    try {
      event = new CustomEvent('cart:error', { bubbles: true, detail: detail });
    } catch (_) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent('cart:error', true, true, detail);
    }
    document.dispatchEvent(event);
  };

  // Map<HTMLElement product-form-component, ProductConfigurator> — kept outside
  // the constructor so the global submit guard can find the right instance.
  var instances = new Map();

  function installGlobalSubmitGuard() {
    if (window.__configuratorSubmitGuardInstalled) return;
    window.__configuratorSubmitGuardInstalled = true;

    // Window-level capture fires BEFORE document-level capture, which is where
    // savor's component framework dispatches `submit`. Intercepting here lets us
    // beat savor's `handleSubmit` and run our own bundle add-to-cart instead.
    window.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        var pf = form.closest(SELECTORS.productForm);
        if (!pf) return;

        var instance = instances.get(pf);
        if (!instance) {
          log('submit: no configurator instance for form', pf);
          return;
        }
        if (!instance.hasAnySelectedAddon()) {
          log('submit: no addons selected — letting savor handle');
          return;
        }

        log('submit: intercepting bundle ATC');
        e.preventDefault();
        e.stopImmediatePropagation();
        if (typeof e.stopPropagation === 'function') e.stopPropagation();

        instance.bundleAddToCart()
          .catch(function (err) {
            console.error('[product-configurator] Bundle add to cart failed:', err);
            document.dispatchEvent(new CustomEvent('cart:error', {
              bubbles: true,
              detail: { message: (err && err.message) || 'Bundle add to cart failed' },
            }));
          })
          .finally(function () {
            var atc = form.querySelector('add-to-cart-component button, button[type="submit"]');
            if (atc) {
              atc.disabled = false;
              atc.removeAttribute('aria-busy');
              atc.classList.remove('loading');
            }
          });
      },
      { capture: true }
    );
  }

  function init() {
    installGlobalSubmitGuard();

    var forms = document.querySelectorAll(SELECTORS.productForm);
    log('init: found', forms.length, 'product-form-component(s)');

    // Global doc-wide check: are there ANY config blocks on the page?
    var globalConfigCount = document.querySelectorAll('.config-group, .config-quantity-item').length;
    var globalBasePriceMarkers = document.querySelectorAll('[data-config-base-price]').length;
    log(
      'init: document scan —',
      globalConfigCount,
      '.config-group/.config-quantity-item element(s),',
      globalBasePriceMarkers,
      'data-config-base-price marker(s)'
    );

    if (globalConfigCount === 0) {
      log(
        'init: WARNING — no .config-group divs in the DOM. The script tag is injected per block, so',
        'if you see this but the script is loading, the {% if block_settings.options/collection %} guard',
        'in the block Liquid is evaluating false (e.g., collection handle is empty).'
      );
    }

    forms.forEach(function (pf) {
      if (pf.dataset.configuratorInitialized === '1') return;

      // If there are NO config blocks anywhere in the document, skip — nothing to do.
      if (globalConfigCount === 0) {
        log('init: skipping pf, no config blocks anywhere on the page', pf);
        return;
      }

      pf.dataset.configuratorInitialized = '1';
      try {
        var instance = new ProductConfigurator(pf);
        instances.set(pf, instance);
        log(
          'init: instance created. base price =',
          instance.basePriceCents,
          'cents. section =',
          instance.section
        );
      } catch (err) {
        console.error('[product-configurator] init failed:', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Already loaded — schedule init after current task.
    setTimeout(init, 0);
  }

  // Re-run init when sections are re-rendered (theme editor / variant change).
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:block:select', init);
  }
})();
